import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { makeXHRCall } from '@wix/da-shared-react/pkg/simpleXHR/actionCreators';
import {
  SimpleXHREndpoint,
  SimpleXHRMethod,
} from '@wix/da-shared-react/pkg/simpleXHR/constants';
import { BiData, EndDiscountConfirmClickBiEvent } from '@wix/da-bi/pkg/events';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { pushGlobalConfirmationModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { Alert as AlertIllustration } from '@wix/da-ds/pkg/Icons/Confirmations/Alert';
import { DiscountFormField } from '../types';
import useValidation from './useValidation';
import yupResolver from '@wix/da-shared-react/pkg/utils/yupResolver';
import { StudioSection } from '../../../../types/store';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { useTranslation } from 'react-i18next';

export function useCreateForm(defaultValues) {
  const validationSchema = useValidation();

  return useForm<{
    [key in DiscountFormField]: any;
  }>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema, {
      abortEarly: false,
    }),
    defaultValues,
  });
}

export const useButtonVisibility = (activeSection, isActive) => ({
  showEdit: activeSection !== StudioSection.SELLER_DISCOUNTS_ENDED,
  showDuplicate: true,
  // We show both end and delete on the active and scheduled tabs
  showDelete:
    [
      StudioSection.SELLER_DISCOUNTS_SCHEDULED,
      StudioSection.SELLER_DISCOUNTS_ACTIVE_AND_SCHEDULED,
    ].includes(activeSection) && !isActive,
  showEndDiscount:
    [
      StudioSection.SELLER_DISCOUNTS_ACTIVE,
      StudioSection.SELLER_DISCOUNTS_ACTIVE_AND_SCHEDULED,
    ].includes(activeSection) && isActive,
});

export const useDeleteDiscount = () => {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const dispatchMakeXHRCall = useAction(makeXHRCall);
  const dispatchAddErrorNotifications = useAction(addErrorNotification);
  const dispatchPushGlobalConfirmationModal = useAction(
    pushGlobalConfirmationModal
  );

  const deleteDiscount = useCallback(
    (discountId: number) => {
      setIsBusy(true);
      dispatchPushGlobalConfirmationModal({
        icon: <AlertIllustration />,
        titleTransKey: 'studio.deleteDiscountConfirmation.title',
        textTransKey: 'studio.deleteDiscountConfirmation.text',
        confirmBtnLabelTransKey:
          'studio.deleteDiscountConfirmation.confirmButton.label',
        confirmBtnType: 'danger',
        cancelBtnLabelTransKey:
          'studio.deleteDiscountConfirmation.cancelButton.label',
        withCancelButton: true,
        onConfirm: () =>
          dispatchMakeXHRCall({
            method: SimpleXHRMethod.POST,
            endpoint: SimpleXHREndpoint.SELLER_DISCOUNT_DELETE,
            papiBase: 'studio',
            data: { discount_id: discountId },
            onSuccess: () => window.location.reload(),
            onError: () => {
              setIsBusy(false);
              dispatchAddErrorNotifications(
                t('studio.deleteDiscountConfirmation.error')
              );
            },
          }),
        onCancel: () => setIsBusy(false),
      });
    },
    [
      dispatchMakeXHRCall,
      dispatchAddErrorNotifications,
      dispatchPushGlobalConfirmationModal,
      t,
    ]
  );

  return {
    isBusy,
    deleteDiscount,
  };
};

export const useEndDiscount = () => {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const dispatchMakeXHRCall = useAction(makeXHRCall);
  const dispatchAddErrorNotifications = useAction(addErrorNotification);
  const dispatchPushGlobalConfirmationModal = useAction(
    pushGlobalConfirmationModal
  );

  return {
    isBusy,
    endDiscount: useCallback(
      (discountId: number) => {
        setIsBusy(true);
        dispatchPushGlobalConfirmationModal({
          icon: <AlertIllustration />,
          titleTransKey: 'studio.endDiscountConfirmation.title',
          textTransKey: 'studio.endDiscountConfirmation.text',
          confirmBtnLabelTransKey:
            'studio.endDiscountConfirmation.confirmButton.label',
          confirmBtnType: 'danger',
          confirmBtnBiData: BiData<EndDiscountConfirmClickBiEvent>({
            evid: 454,
            campaignid: discountId,
          }),
          cancelBtnLabelTransKey:
            'studio.endDiscountConfirmation.cancelButton.label',
          withCancelButton: true,
          onConfirm: () =>
            dispatchMakeXHRCall({
              method: SimpleXHRMethod.POST,
              endpoint: SimpleXHREndpoint.SELLER_DISCOUNT_END,
              papiBase: 'studio',
              data: { discount_id: discountId },
              onSuccess: () => {
                window.location.reload();
              },
              onError: () => {
                setIsBusy(false);
                dispatchAddErrorNotifications(
                  t('studio.endDiscountConfirmation.error')
                );
              },
            }),
          onCancel: () => setIsBusy(false),
        });
      },
      [
        dispatchMakeXHRCall,
        dispatchAddErrorNotifications,
        dispatchPushGlobalConfirmationModal,
        t,
      ]
    ),
  };
};

export const useDiscountRowActions = (discountId: number) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'edit' | 'duplicate'>('edit');

  const openEditModal = useCallback(() => {
    setModalMode('edit');
    setIsModalOpen(true);
  }, []);

  const openDuplicateModal = useCallback(() => {
    setModalMode('duplicate');
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return {
    isModalOpen,
    modalMode,
    openEditModal,
    openDuplicateModal,
    closeModal,
  };
};
